import { useMemo } from 'react';

import { Box, ButtonMantine, Divider, Stack, Text } from 'ui';

import { usePlatformService } from '../../../hooks';
import { AppleWalletIcon } from './AppleWalletIcon';
import { EventDateTime } from './EventDateTime';
import { EventLocation } from './EventLocation';
import { TicketDetails } from './TicketDetails';
import { EventTicketsPrintButton } from './pdf/EventTicketsPrintButton';
import type { EventTicketInfo } from './pdf/eventTicketsPdf.types';

interface EventTicketInfoProps extends EventTicketInfo {
  onAddTicketToWallet?: () => void;
}

const EventTicketInfo = ({
  title,
  subtitle,
  eventStartDate,
  venueName,
  addressLineOne,
  addressLineTwo,
  city,
  state,
  zipCode,
  ticketName,
  ticketId,
  ticketUrl,
  isUsed,
  isExpired,
  onAddTicketToWallet,
  ticketQrCodeUrl,
}: EventTicketInfoProps) => {
  const Platform = usePlatformService();

  const isValidTicket = useMemo(() => {
    return !(isUsed || isExpired);
  }, [isUsed, isExpired]);

  /* Show download button on desktop browsers */
  const showDownloadButton = useMemo(() => {
    return Platform.isDesktopBrowser && isValidTicket;
  }, [Platform, isValidTicket]);

  /* Show add to wallet button on mobile browsers/native */
  const showAddToWalletButton = useMemo(() => {
    return !Platform.isDesktopBrowser && isValidTicket && !!onAddTicketToWallet;
  }, [Platform, isValidTicket, onAddTicketToWallet]);

  const isLocationEmpty = useMemo(
    () => !venueName && !addressLineOne && !city && !state && !zipCode,
    [venueName, addressLineOne, city, state, zipCode]
  );

  const event = useMemo(
    () => ({
      title,
      subtitle,
      eventStartDate,
      venueName,
      addressLineOne,
      addressLineTwo,
      city,
      state,
      zipCode,
      ticketName,
      ticketId,
      ticketUrl,
      isUsed,
      isExpired,
      ticketQrCodeUrl,
    }),
    [
      title,
      subtitle,
      eventStartDate,
      venueName,
      addressLineOne,
      addressLineTwo,
      city,
      state,
      zipCode,
      ticketName,
      ticketId,
      ticketUrl,
      isUsed,
      isExpired,
      ticketQrCodeUrl,
    ]
  );

  return (
    <Stack gap="sm" p="md">
      <Stack gap="4">
        <Text fz="lg" fw="600" ta="center">
          {title || 'Your Event'}
        </Text>
        {!!subtitle && (
          <Text fz="xs" fw={500} ta="center">
            {subtitle}
          </Text>
        )}
      </Stack>

      <Box>
        <EventDateTime startDate={eventStartDate} />

        {!isLocationEmpty && (
          <>
            <Divider />
            <EventLocation
              venueName={venueName}
              addressLineOne={addressLineOne}
              addressLineTwo={addressLineTwo}
              city={city}
              state={state}
              zipCode={zipCode}
            />
          </>
        )}

        <TicketDetails ticketName={ticketName} ticketId={ticketId} ticketUrl={ticketUrl} />
      </Box>

      {showAddToWalletButton && (
        <ButtonMantine
          style={{ backgroundColor: 'black' }}
          size="md"
          mb={'sm'}
          radius={8}
          fullWidth
          onClick={onAddTicketToWallet}
        >
          <AppleWalletIcon />
        </ButtonMantine>
      )}
      {/*
          Future State: Native actions will be to add to wallet while other actions will be to download the ticket
      */}
      {showDownloadButton && (
        <EventTicketsPrintButton
          title={title}
          eventStartDate={eventStartDate}
          eventList={[event]}
          venueName={venueName}
          addressLineOne={addressLineOne}
          city={city}
          state={state}
          zipCode={zipCode}
        />
      )}
    </Stack>
  );
};

export default EventTicketInfo;
